/**
 * HydraBaseNode - Base class for all Hydra nodes
 *
 * Extends VideoBaseNode to provide renderToCanvas functionality.
 * Contains cached Hydra instance and chain evaluation logic.
 */


import type { Dependencies } from '@nodes/Dependencies';
import type { EndpointsRecord } from '@nodes/TypedEndpoints';
import { BaseNode } from '@/nodes/BaseNode';


/**
 * Base class for all Hydra nodes.
 * Provides arg getter creation for Hydra function parameters.
 * Chain building is handled by HydraRenderer via graph query.
 */
export abstract class HydraBaseNode<T extends EndpointsRecord> extends BaseNode<T> {

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  /**
   * Create Hydra arg getter functions for the given endpoint names.
   * Each getter receives Hydra's props ({ time, bpm, resolution, mouse })
   * and returns the current param value. If the param value is a function,
   * it's called with the props — enabling animated/time-based params.
   */
  protected createArgGetters(fnArgs: string[]): ((props: any) => any)[] {
    return fnArgs.map(name => {
      const accessor = (this.params as any)[name];
      return (props: any) => {
        const val = accessor?.value;
        return typeof val === 'function' ? val(props) : val;
      };
    });
  }
}
